import React from 'react';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { observer } from 'mobx-react-lite';
import { defineMessages } from 'react-intl';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import {
    CalculatorStepPanel,
    Sizes,
    Question,
    useForm,
    YEAR_FORMAT,
    HiddenField,
    FormComputed,
    MessageBanner,
    MessageBannerVariants,
    MessageBannerCTAVariants,
    BOOLEAN_OPTIONS,
} from '@ratehub/base-ui';
import {
    MORTGAGE_PLACEHOLDERS,
} from '@ratehub/mtg-common';

import CalculatorResultsBox from './CalculatorResultsBox';


const CONTRIBUTION_START_YEAR = 2009; // first year that Canadians could contribute to tfsa

function ContributionLimitTFSACalcInputStep({
    messageBannerProps,
    ctaHref,
    ctaLabel,
    currentYear,
    contributionLimits,
}) {
    const form = useForm();

    const isMessageBannerCTAEnabled = !!(messageBannerProps?.title || messageBannerProps?.message);

    return (
        <Container
            stepNumber={1}
            isVisible
        >
            <section className="header">
                <FormattedMessage
                    id="calculators.Contribution-Limit-TFSA.header"
                    defaultMessage="Calculate your contribution room"
                />
            </section>

            <hr className="rh-horizontal-rule rh-mb-1_5 rh-width-100p" />

            <HiddenField
                name="currentYear"
                value={currentYear}
            />

            <HiddenField
                name="contributionLimits"
                value={contributionLimits}
            />

            <Question
                className="question-container"
                field={{
                    name: 'isResident',
                    type: 'boolean',
                    options: BOOLEAN_OPTIONS,
                }}
                type="radio"
                labelMessage={(
                    <FormattedMessage
                        id="calculators.Contribution-Limit-TFSA.residentOfCanadaBefore2010Label"
                        defaultMessage="Were you a resident of Canada before 2010?"
                    />
                )}
                flowDirection="row"
                isSideBySide={true}
                tooltipMessage={(
                    <FormattedMessage
                        id="calculators.Contribution-Limit-TFSA.residentOfCanadaBefore2010Tooltip"
                        defaultMessage="The TFSA program was launched in 2009, and your contribution
                                        limit will depend on whether you started living in Canada before 2010."
                    />
                )}
            />

            <Question
                className="question-container"
                field={{
                    name: 'yearOfResidency',
                    type: 'date',
                    dateFormat: YEAR_FORMAT,
                    validators: [
                        {
                            fn: isYearValid,
                        },
                        {
                            fn: (date) => date.getFullYear() > dayjs('2009').year(),
                            message: (
                                <FormattedMessage
                                    id="calculators.Contribution-Limit-TFSA.yearLessThan2010ErrorMessage"
                                    defaultMessage="Only required if you moved to Canada after 2009"
                                />),
                        },
                    ],
                    defaultErrorMessage:MESSAGES.DATE_VALIDATION,
                }}
                labelMessage={(
                    <FormattedMessage
                        id="calculators.Contribution-Limit-TFSA.yearOfResidencyLabel"
                        defaultMessage="What year did you become a resident of Canada?"
                    />
                )}
                isSideBySide={true}
                quicktipMessage={(
                    <FormattedMessage
                        id="calculators.Contribution-Limit-TFSA.yearOfResidencyQuicktip"
                        defaultMessage="In order to determine your TFSA limit, we need the year you started living in Canada."
                    />
                )}
                isVisible={form.values.isResident === false}
            />

            <Question
                className="question-container"
                field={{
                    name: 'birthYear',
                    type: 'date',
                    dateFormat: YEAR_FORMAT,
                    validators: [
                        {
                            fn: isYearValid,
                        },
                        {
                            fn: (date) => dayjs().year() - date.getFullYear() >= 18,

                            message: (
                                <FormattedMessage
                                    id="calculators.Contribution-Limit-TFSA.underageErrormessage"
                                    defaultMessage="You must be at least 18"
                                />
                            ),
                        } ],
                    defaultErrorMessage:MESSAGES.DATE_VALIDATION,
                }}
                labelMessage={(
                    <FormattedMessage
                        id="calculators.Contribution-Limit-TFSA.birthYearLabel"
                        defaultMessage="What year were you born?"
                    />
                )}
                isSideBySide={true}
                tooltipMessage={(
                    <FormattedMessage
                        id="calculators.Contribution-Limit-TFSA.birthYearTooltip"
                        defaultMessage="We will use your birth year to determine
                                        which year you turned 18 years old -
                                        which can affect your total TFSA contribution limit."
                    />
                )}
            />

            <Question
                className="question-container"
                field={{
                    name: 'tfsaContribution',
                    type: 'integer',
                    required: false,
                }}
                labelMessage={(
                    <FormattedMessage
                        id="calculators.Contribution-Limit-TFSA.tfsaContributionLabel"
                        defaultMessage="How much have you contributed to your TFSA so far?"
                    />
                )}
                placeholder={MORTGAGE_PLACEHOLDERS.AMOUNT_PLACEHOLDER}
                quicktipMessage={(
                    <FormattedMessage
                        id="calculators.Contribution-Limit-TFSA.tfsaContributionQuicktip"
                        defaultMessage="Total amount deposited across all your TFSA accounts (not including gains)."
                    />
                )}
                type="currency"
                isSideBySide={true}
                showOptionalText={false}
            />

            <Question
                className="question-container"
                field={{
                    name: 'hasWithdrawnContributions',
                    type: 'boolean',
                    options: BOOLEAN_OPTIONS,
                }}
                type="radio"
                labelMessage={(
                    <FormattedMessage
                        id="calculators.Contribution-Limit-TFSA.WithdrawalBooleanLabel"
                        defaultMessage="Have you ever made a withdrawal from your TFSA?"
                    />
                )}
                flowDirection="row"
                isSideBySide={true}
                tooltipMessage={(
                    <FormattedMessage
                        id="calculators.Contribution-Limit-TFSA.WithdrawalBooleanTooltip"
                        defaultMessage="Taking money out of your TFSA will affect your total contribution limit."
                    />
                )}
            />

            <Question
                className="question-container"
                field={{
                    name: 'previousWithdrawalValue',
                    type: 'integer',
                    required: false,
                }}
                labelMessage={(
                    <FormattedMessage
                        id="calculators.Contribution-Limit-TFSA.previousWithdrawalValueLabel"
                        defaultMessage="How much did you withdraw in previous years?"
                    />
                )}
                placeholder={MORTGAGE_PLACEHOLDERS.AMOUNT_PLACEHOLDER}
                tooltipMessage={(
                    <FormattedMessage
                        id="calculators.Contribution-Limit-TFSA.previousWithdrawalValueTooltip"
                        defaultMessage="Any contribution room deducted from previous years'
                                        withdrawl are regained (e.g. if you withdrew $5,000
                                        last year, your total TFSA contribution room for
                                        this year will increase by $5,000)."
                    />
                )}
                type="currency"
                isSideBySide={true}
                isVisible={!!form.values.hasWithdrawnContributions}
                showOptionalText={false}
            />
            <FormComputed
                name="isValidAge"
                value={getIsValidAge}
                hidden
            />

            <FormComputed
                name="eligibleYear"
                value={getEligibleYear}
                hidden
            />

            <FormComputed
                name="maxLimit"
                value={calculateMaxLimit}
                hidden
            />

            <FormComputed
                name="totalContributionRoom"
                value={calculateTotalContributionRoom}
                hidden
            />

            <FormComputed
                name="resultWarning"
                value={getResultsWarning}
                hidden
            />

            <CalculatorResultsBox
                className="box"
                resultMessage={(
                    <FormattedMessage
                        id="calculators.Contribution-Limit-TFSA.resultMessage"
                        defaultMessage={'Your contribution room for {year}'}
                        values={{ year: dayjs().year() }}
                    />
                )}
                resultValue={form.values.totalContributionRoom >= 0 ? form.values.totalContributionRoom : undefined}
                resultWarningMessage={form.values.resultWarning}
            />

            <If condition={isMessageBannerCTAEnabled}>
                <MessageBanner
                    className="messageBanner"
                    variant={MessageBannerVariants.NOTIFICATION}

                    {...messageBannerProps}
                    messageSize="small"

                    ctaMessage={messageBannerProps?.ctaMessage ?? ctaLabel}
                    ctaDataName="contribution-limit-tfsa-learnMore"
                    ctaVariant={MessageBannerCTAVariants.PRIMARY_ANCHOR}
                    href={messageBannerProps?.href ?? ctaHref}
                />
            </If>
        </Container>
    );
}

ContributionLimitTFSACalcInputStep.propTypes = {
    messageBannerProps: PropTypes.shape({
        title: PropTypes.string,
        message: PropTypes.string,
        href: PropTypes.string,
        ctaMessage: PropTypes.string,
    }),
    ctaHref: PropTypes.string,
    ctaLabel: PropTypes.string,

    currentYear: PropTypes.number,
    contributionLimits: PropTypes.arrayOf(PropTypes.shape({
        year: PropTypes.number,
        contribution: PropTypes.number,
    })),
};

ContributionLimitTFSACalcInputStep.defaultProps = {
    messageBannerProps: undefined,
    ctaHref: undefined,
    ctaLabel: undefined,

    currentYear: undefined,
    contributionLimits: undefined,
};

const MESSAGES = defineMessages({
    DATE_VALIDATION: {
        id: 'calculators.Contribution-Limit-TFSA.yearInvalidMessage',
        defaultMessage: 'Please enter a valid year',
    },
});

//validates if user inputted year is invalid by checking if its in the future
function isYearValid(date) {
    return date.getFullYear() <= dayjs().year();
}

//validates if user is 18
function getIsValidAge({ currentYear, birthYear }) {
    return (dayjs(`${currentYear}`).year() - birthYear?.getFullYear() >= 18);
}

function getEligibleYear({ isResident, yearOfResidency, birthYear, isValidAge }) {
    const yearUserTurnsEighteen = birthYear?.getFullYear() + 18;
    const yearUserBecameResident = yearOfResidency?.getFullYear();

    /* Function can't return an eligible year if user does not enter a valid year they became resident.
        Invalid year of becoming resident is a year less than 2009 or an undefined year input
    */
    if (!isValidAge || yearUserBecameResident <= 2009 || (isResident === false && (yearUserBecameResident == undefined))) {
        return null;
    }

    if (isResident) {
        return (yearUserTurnsEighteen <= CONTRIBUTION_START_YEAR
            ? CONTRIBUTION_START_YEAR
            : yearUserTurnsEighteen);
    } else {
        return (Math.max(yearUserTurnsEighteen, yearUserBecameResident));
    }
}

function calculateMaxLimit({ eligibleYear, contributionLimits, hasWithdrawnContributions, currentYear }) {
    if (eligibleYear == null || hasWithdrawnContributions == null) {
        return null;
    }

    const index = eligibleYear - CONTRIBUTION_START_YEAR; //finds the index of array from the year when user is first eligible
    const lastIndex = currentYear - CONTRIBUTION_START_YEAR; //finds the index of the current year in the contrbutionlimits array
    let maxLimitValue = 0;

    // sum the values of contribution limits from when the user is first eligible to current year
    for (let i = index; i <= lastIndex; i++) {
        maxLimitValue += contributionLimits?.[i].contribution;
    }

    return (maxLimitValue);
}

function calculateTotalContributionRoom({ maxLimit, tfsaContribution, currentWithdrawalValue, previousWithdrawalValue }) {
    if (maxLimit == null) {
        return undefined;
    }

    // If user does not input a value for these fields, they will be assumed to be 0
    const tfsaContributionValue = tfsaContribution ?? 0;
    const currentWithdrawal = currentWithdrawalValue ?? 0;
    const previousWithdrawal = previousWithdrawalValue ?? 0;

    return (maxLimit - tfsaContributionValue - currentWithdrawal + previousWithdrawal); //returns total contribution
}

function getResultsWarning({ totalContributionRoom }) {
    if (totalContributionRoom >= 0) {
        return undefined;
    }

    if (totalContributionRoom < 0) {
        return (
            <FormattedMessage
                id="calculators.Contribution-Limit-TFSA.resultWarningMessage"
                defaultMessage="You have no available contribution room this year or over contributed."
            />
        );
    }
}

const Container = styled(CalculatorStepPanel)`
    padding: ${Sizes.SPACING.ONE_AND_A_HALF} ${Sizes.SPACING.ONE_AND_A_HALF} 0;

    > .header {
        margin-bottom: ${Sizes.SPACING.ONE_AND_A_HALF};
    }

    .question-container {
        margin-top: 0;
        margin-bottom: ${Sizes.SPACING.TWO};
    }

    > .box {
        margin-top: 0;
        margin-bottom: ${Sizes.SPACING.ONE_AND_A_HALF};
    }

    > .messageBanner {
        margin-bottom: ${Sizes.SPACING.TWO};
    }
`;

export default observer(ContributionLimitTFSACalcInputStep);
